import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Badge from 'components/consumer/Badge';
import useKeyboardControlForTabs from 'hooks/useKeyboardControlForTabs';
import PDPNotification from 'components/consumer/PDP/PDPNotification';
import BrandTooltip from 'components/consumer/BrandTooltip';
import useScreen from 'hooks/useScreen';

const ToggleTabGroup = ({
  'aria-label': ariaLabel,
  className = '',
  items,
  noBottomBorder,
  selectedTab,
  size = 'lg',
  styling,
  variant = 'tabs',
  tabClassNamesOverride,
  tabGroupsClassNamesOverride,
  selectedTabClassNamesOverride,
}) => {
  const tabsRef = useRef(null);
  useKeyboardControlForTabs(tabsRef);

  const [showMobileTooltip, setShowMobileTooltip] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState('');
  const { isMobileOrTablet } = useScreen();

  const onTooltipToggle = (isTooltipOpen, title) => {
    setTooltipTitle(title);
    setShowMobileTooltip(isTooltipOpen);
  };

  const closeTooltip = () => {
    setShowMobileTooltip(false);
  };

  const isImportantToggle = styling === 'ImportantToggle';
  const isButtonsVariant = variant === 'buttons';

  return (
    <>
      {showMobileTooltip && isMobileOrTablet && (
        <PDPNotification
          heading="Ready to ship"
          subheading={tooltipTitle}
          onClose={closeTooltip}
        />
      )}
      <div
        className={classNames(`${className}`, {
          'md:border-solid': !noBottomBorder,
          'md:border-b-2 md:border-gray-light6':
            !noBottomBorder && !isButtonsVariant,
          'w-[inherit] overflow-x-auto pb-0.5 md:pb-[35px] md:border-b md:border-gray-light7':
            !noBottomBorder && isButtonsVariant,
        })}
      >
        <div
          className={classNames('list-none m-0 p-0 flex flex-row', {
            'justify-start gap-4': items?.length < 3,
            'justify-between': items?.length >= 3,
            [tabGroupsClassNamesOverride]: !!tabGroupsClassNamesOverride,
            'items-stretch h-[60px]': size === 'md',
          })}
          role="tablist"
          aria-label={ariaLabel}
          ref={tabsRef}
        >
          {items.map(
            ({
              id,
              label,
              desc,
              tabClass,
              onClick,
              badgeValue,
              contentId,
              customComponent,
              infoTooltip = false,
              infoTooltipContent = '',
            }) => {
              const isSelectedItem = selectedTab === id;

              return (
                <div
                  className={classNames(
                    'flex justify-between items-stretch relative',
                    {
                      'flex-1 md:flex-none': !isImportantToggle,
                      'flex-none': isImportantToggle,
                    }
                  )}
                  key={id}
                >
                  <button
                    aria-controls={contentId}
                    aria-selected={isSelectedItem ? 'true' : 'false'}
                    className={classNames(
                      'text-sm list-none font-bold items-center bg-transparent -bottom-0.5 text-gray flex flex-1 justify-center relative text-center whitespace-nowrap',
                      'md:text-lg md:flex-none',
                      'hover:cursor-pointer',
                      'focus-visible:[border-bottom:2px_solid_#107c8c] focus-visible:text-brand',
                      '[&:not(:focus-visible)]:focus:[border-bottom:2px_solid_#107c8c]',
                      {
                        'border-b-2': !isButtonsVariant && !isImportantToggle,
                        'border-transparent':
                          !isButtonsVariant && !isSelectedItem,
                        // Buttons variant
                        'mr-3.5 rounded-[5px]': isButtonsVariant,
                        'hover:border-gray':
                          isButtonsVariant || !isSelectedItem,
                        'mt-px mb-px border-2 border-gray-light3':
                          isButtonsVariant && !isSelectedItem,
                        'mt-0 mb-0 border-[3px] border-brand':
                          isButtonsVariant && isSelectedItem,
                        // Tabs variant
                        'px-8': !customComponent && !isImportantToggle,
                        'p-[.8rem] md:px-8 md:py-6':
                          !customComponent && !isImportantToggle,
                        'py-4': isImportantToggle,
                        // Tabs variant - selected
                        'border-brand hover:border-brand-dark': isSelectedItem,
                        [selectedTabClassNamesOverride]:
                          !!selectedTabClassNamesOverride && isSelectedItem,
                        // ImportantToggle
                        '!text-lg text-gray-light2 flex-none': isImportantToggle,
                        'hover:text-brand-dark hover:cursor-pointer': isImportantToggle,
                        'focus-visible:border-b-2 focus-visible:border-brand focus-visible:text-brand': isImportantToggle,
                        '[&:not(:focus-visible)]:focus:border-brand': isImportantToggle,
                        'border-b hover:border-b-2 hover:border-brand-dark':
                          isImportantToggle && !isSelectedItem,
                        'border-b-2 border-brand-dark text-gray':
                          isImportantToggle && isSelectedItem,
                        // Sizes
                        '!text-sm leading-[1.5rem] tracking-[.0187rem] items-center flex px-8 py-0':
                          size === 'md',
                        // Custom class
                        [tabClass]: !!tabClass,
                        [tabClassNamesOverride]: !!tabClassNamesOverride,
                      }
                    )}
                    id={id}
                    onClick={onClick}
                    role="tab"
                    tabIndex={isSelectedItem ? undefined : '-1'}
                    type="button"
                  >
                    {customComponent || label}

                    {!!badgeValue && (
                      <Badge className="ml-2">{badgeValue}</Badge>
                    )}

                    {desc && (
                      <div className="text-xs font-bold text-gray">{desc}</div>
                    )}
                  </button>

                  {infoTooltip ? (
                    <div
                      className={classNames(
                        'items-center cursor-pointer flex justify-center z-[1000] [&_svg]:!ml-1 [&_svg]:max-[767px]:!mr-0 top-[calc(50%_-_9px)]'
                      )}
                    >
                      <BrandTooltip
                        inactiveOnMobile
                        onTooltipToggle={onTooltipToggle}
                        title={infoTooltipContent}
                        width={300}
                        iconSize="big"
                      />
                    </div>
                  ) : (
                    <div />
                  )}
                </div>
              );
            }
          )}
        </div>
      </div>
    </>
  );
};

ToggleTabGroup.propTypes = {
  'aria-label': PropTypes.string.isRequired,
  className: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      contentId: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      desc: PropTypes.string,
      tabClass: PropTypes.string,
      onClick: PropTypes.func,
      badgeValue: PropTypes.node,
      customComponent: PropTypes.node,
      infoTooltip: PropTypes.bool,
      infoTooltipContent: PropTypes.string,
    })
  ).isRequired,
  noBottomBorder: PropTypes.bool,
  selectedTab: PropTypes.string,
  size: PropTypes.oneOf(['md', 'lg']),
  styling: PropTypes.oneOf(['ImportantToggle']),
  variant: PropTypes.oneOf(['tabs', 'buttons']),
  tabClassNamesOverride: PropTypes.string,
  tabGroupsClassNamesOverride: PropTypes.string,
  selectedTabClassNamesOverride: PropTypes.string,
};

export default ToggleTabGroup;
